import { DesignSystemProvider } from "@albertine/design-system/providers/DesignSystemProvider";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import {
    createRootRouteWithContext,
    HeadContent,
    Outlet,
    retainSearchParams,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import {
    AlbertineI18nProvider,
    ServiceLanguage,
    Tenant,
    TenantProvider,
} from "albertine-shared-web";
import { lazy, Suspense } from "react";
import { z } from "zod";
import { DEV_MEMBER_LOGIN_ENABLED } from "@/lib/devMemberLogin/devMemberLoginEnabled";
import { loadTenantCSS } from "@/lib/loadTenantCSS";
import TanStackQueryDevtools from "../integrations/tanstack-query/devtools";

// Loaded lazily so that only the local dev server ever bundles the member login.
const DevMemberPanel = DEV_MEMBER_LOGIN_ENABLED
    ? lazy(async () => {
          const { DevMemberPanel } = await import(
              "@/lib/devMemberLogin/DevMemberPanel"
          );
          return { default: DevMemberPanel };
      })
    : null;

interface MyRouterContext {
    queryClient: QueryClient;
}

const Params = z.object({
    tenant: Tenant,
    locale: ServiceLanguage,
    parentOrigin: z.url().optional(),
    itemLinkPrefix: z
        .string()
        .regex(/^\/[^\s]*$/)
        .optional(),
});

export const Route = createRootRouteWithContext<MyRouterContext>()({
    validateSearch: Params,

    search: {
        middlewares: [
            retainSearchParams(["tenant", "locale", "parentOrigin", "itemLinkPrefix"]),
        ],
    },

    head: () => {
        return {
            meta: [
                {
                    title: "Albertine Mini Apps",
                },
            ],
        };
    },

    beforeLoad: async ({ search }) => {
        await loadTenantCSS(search.tenant);
    },

    component: () => {
        const { locale, tenant } = Route.useSearch();

        return (
            <AlbertineI18nProvider initialLanguage={locale}>
                <DesignSystemProvider tenant={tenant}>
                    <TenantProvider value={{ tenant, dependencyInjection: undefined }}>
                        <HeadContent />
                        <Outlet />
                        <TanStackDevtools
                            config={{
                                position: "bottom-right",
                            }}
                            plugins={[
                                ...(DevMemberPanel
                                    ? [
                                          {
                                              name: "Member",
                                              defaultOpen: true,
                                              render: (
                                                  _element: HTMLElement,
                                                  theme: "dark" | "light",
                                              ) => (
                                                  <Suspense>
                                                      <DevMemberPanel theme={theme} />
                                                  </Suspense>
                                              ),
                                          },
                                      ]
                                    : []),
                                {
                                    name: "Tanstack Router",
                                    render: <TanStackRouterDevtoolsPanel />,
                                },
                                TanStackQueryDevtools,
                            ]}
                        />
                    </TenantProvider>
                </DesignSystemProvider>
            </AlbertineI18nProvider>
        );
    },
});
